import * as React from 'react';
import LockStack from './LockStack.js';
import wrapFocus from './util/wrapFocus.js';

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
// active stacks does not make sense, as documents are only capable of having one
// activeElement at a time.

var LOCK_STACK = new LockStack();
var lockCount = 0;

function newLockUID() {
  return "lock-".concat(lockCount++);
}
/**
 * Creates a subscription to the lock stack that is bound to the lifetime
 * of the caller, based on `useEffect`.
 */


function useLockSubscription(callback) {
  // `subscribe` returns an `unsubscribe` function that `useEffect` can invoke
  // to clean up the subscription.
  React.useEffect(function () {
    return LOCK_STACK.subscribe(callback);
  }, [callback]);
}
/**
 * Set up a return of focus to the target element specified by `returnTo` to
 * occur at the end of the lifetime of the caller component. In other words,
 * return focus to where it was before the caller component was mounted.
 */

function useFocusReturn(returnRef, disabledRef, getReturnRef) {
  // This isn't necessarily safe, but realistically it's sufficient.
  var _React$useState = React.useState(function () {
    return document.activeElement;
  }),
      _React$useState2 = _slicedToArray(_React$useState, 1),
      target = _React$useState2[0];

  React.useLayoutEffect(function () {
    return function () {
      if (disabledRef != null && disabledRef.current) return; // Happens on next tick to ensure it is not overwritten by focus lock.

      if (returnRef != null && returnRef.current != null) {
        returnRef.current.focus();
        return;
      }

      if (getReturnRef) {
        var targetReturnRef = getReturnRef();

        if (targetReturnRef) {
          var _targetReturnRef$curr, _targetReturnRef$curr2;

          targetReturnRef === null || targetReturnRef === void 0 ? void 0 : (_targetReturnRef$curr = targetReturnRef.current) === null || _targetReturnRef$curr === void 0 ? void 0 : (_targetReturnRef$curr2 = _targetReturnRef$curr.focus) === null || _targetReturnRef$curr2 === void 0 ? void 0 : _targetReturnRef$curr2.call(_targetReturnRef$curr);
          return;
        }
      }

      target != null && target.focus();
    }; // Explicitly only want this to run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
/**
 * Create and push a new lock onto the global LOCK_STACK, tied to the lifetime
 * of the caller. Returns a ref containing the current enabled state of the
 * layer, to be used for enabling/disabling the caller's lock logic.
 */

function useLockLayer(controlledUID) {
  var _React$useState3 = React.useState(function () {
    return controlledUID || newLockUID();
  }),
      _React$useState4 = _slicedToArray(_React$useState3, 1),
      uid = _React$useState4[0];

  var enabledRef = React.useRef(false); // When first created, immediately disable the current layer (the one below
  // this new one) to prevent its handlers from stealing focus back when this
  // layer mounts. Specifically, this allows `autoFocus` to work, since React
  // will perform the autofocus before any effects have run.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  React.useState(function () {
    return LOCK_STACK.toggleLayer(LOCK_STACK.current(), false);
  });

  React.useLayoutEffect(function () {
    LOCK_STACK.add(uid, function (enabled) {
      return enabledRef.current = enabled;
    });
    return function () {
      return LOCK_STACK.remove(uid);
    };
  }, [uid]);
  return enabledRef;
}
function useFocusLock(containerRef) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var returnRef = options.returnRef,
      disableReturnRef = options.disableReturnRef,
      _options$attachToRef = options.attachToRef,
      attachToRef = _options$attachToRef === void 0 ? containerRef : _options$attachToRef,
      disable = options.disable,
      _options$getInitialFo = options.getInitialFocusElement,
      getInitialFocusElement = _options$getInitialFo === void 0 ? function () {
    return null;
  } : _options$getInitialFo,
      getReturnRef = options.getReturnRef; // Create a new layer for this lock to occupy

  var enabledRef = useLockLayer(); // Allow the caller to override the lock and force it to be disabled.

  React.useEffect(function () {
    if (!disable) return;
    enabledRef.current = false;
  }, [disable]); // Apply the actual lock logic to the container.

  React.useLayoutEffect(function () {
    // Move focus into the container if it is not already, or if an element
    // inside of the container will automatically receive focus, it won't be moved.
    var container = containerRef.current;

    if (container != null && document.activeElement != null && !container.contains(document.activeElement) && container.querySelector('[autofocus]') == null) {
      wrapFocus(container, document.activeElement, true, getInitialFocusElement(container));
    }

    function handleFocusIn(event) {
      if (!enabledRef.current) return;
      var root = containerRef.current;
      if (root == null) return;
      var newFocusElement = event.target || document.body;
      if (root.contains(newFocusElement)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      wrapFocus(root, newFocusElement);
    }

    function handleFocusOut(event) {
      var _event$relatedTarget;

      if (!enabledRef.current) return;
      var root = containerRef.current;
      if (root == null) return; // The element that focus is moving to after blur/focus out

      var newFocusElement = event.relatedTarget || document.body;

      if (event.relatedTarget === document.body) {
        event.preventDefault();
        root.focus();
      }

      if (root.contains(newFocusElement)) return;
      var elementToForceFocusTo = null; // check if there is a previously focused element and if it is contained in
      // our container/locking element. Also check if the newly focused element is one of our focus guards.
      // if the previously focused element is inside our container and it is NOT a focus guard,
      // then move focus back to the element that previously had focus to avoid any erratic focus behavior

      if (event.target && root.contains(event.target) && !((_event$relatedTarget = event.relatedTarget) !== null && _event$relatedTarget !== void 0 && _event$relatedTarget.hasAttribute('data-prism-focus-guard'))) {
        elementToForceFocusTo = event.target;
      }

      wrapFocus(root, newFocusElement, false, elementToForceFocusTo);
    }

    var attachTo = attachToRef.current;
    if (attachTo == null) return undefined;
    attachTo.addEventListener('focusin', handleFocusIn, {
      capture: true
    });
    attachTo.addEventListener('focusout', handleFocusOut, {
      capture: true
    });
    return function () {
      attachTo.removeEventListener('focusin', handleFocusIn, {
        capture: true
      });
      attachTo.removeEventListener('focusout', handleFocusOut, {
        capture: true
      });
    };
  }, [containerRef, attachToRef]); // Set up a focus return after the container is unmounted.
  // This happens at the end to absolutely ensure that the return is the last
  // thing that will run as part of this hook (i.e., that the focus handlers
  // have been fully detached).

  useFocusReturn(returnRef, disableReturnRef, getReturnRef);
}
/**
 * A convenience component for rendering "guard elements" that ensure there is
 * always a tabbable element either before or after (or both) an active lock.
 * These are most easily rendered right at the edges of the React rendering root
 * so that all locks can utilize the same guards.
 *
 * When no lock is active, `FocusGuard` is completely invisible, both visually
 * and in the focus order. When active, it gets a tabindex, but always remains
 * visually hidden.
 */

var FocusGuard = /*#__PURE__*/React.memo(function () {
  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      active = _React$useState6[0],
      setActive = _React$useState6[1];

  useLockSubscription(function (newActiveState) {
    return setActive(newActiveState);
  });
  return /*#__PURE__*/React.createElement("div", {
    tabIndex: active ? 0 : undefined,
    style: {
      position: 'fixed',
      opacity: 0,
      pointerEvents: 'none'
    },
    "data-prism-focus-guard": true
  });
});

export { FocusGuard, LOCK_STACK, useFocusLock as default, useFocusLock, useFocusReturn, useLockLayer, useLockSubscription };
